import { t as localeText } from "@/shared/lib/translate";
import { CloseIcon } from "./icons";
import { SearchIcon } from "@/shared/ui/icons";
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { filterCommands, type Command } from "@/shared/lib/commands";
import { useCommands, usePalette } from "@/shared/lib/hooks/useCommands";
import { cn } from "@/shared/lib/cn";
import { formatShortcut } from "@/shared/lib/shortcut";
import { Kbd, KbdGroup } from "./Kbd";
import { Surface } from "./Surface";
import { Text } from "./Text";
import { TextField } from "./Field";
import { IconButton } from "./IconButton";
import { useOverlayFocus } from "./Dialog";

/**
 * The ⌘K command palette. Reads whatever features have registered into the
 * command store, filters by query (`filterCommands`), and runs the selection.
 * Self-contained (overlay + portal + keyboard nav); mount once in the app shell.
 * Opening is driven by the store (`usePalette`), so any control can summon it.
 */
export function CommandPalette() {
  const { t } = useTranslation();
  const { open, setOpen } = usePalette();
  const commands = useCommands();
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  useOverlayFocus({
    open,
    onClose: () => setOpen(false),
    dismissible: true,
    overlayRef: dialogRef,
    initialFocusRef: inputRef,
    inertBackground: true,
    lockScroll: true,
  });

  const filtered = useMemo(() => filterCommands(commands, query), [commands, query]);

  // Reset when opening; keep the active item in range as the list narrows.
  useEffect(() => {
    if (open) {
      setQuery("");
      setActiveIndex(0);
    }
  }, [open]);
  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  if (!open) return null;

  function run(command: Command | undefined): void {
    if (!command) return;
    setOpen(false);
    command.perform();
  }

  function onKeyDown(event: React.KeyboardEvent<HTMLInputElement>): void {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex((index) => (filtered.length ? (index + 1) % filtered.length : 0));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((index) =>
        filtered.length ? (index - 1 + filtered.length) % filtered.length : 0,
      );
    } else if (event.key === "Enter") {
      event.preventDefault();
      run(filtered[activeIndex]);
    }
  }

  // Group in display order while keeping a flat index for keyboard nav.
  const groups: { group: string; items: { command: Command; index: number }[] }[] = [];
  filtered.forEach((command, index) => {
    let bucket = groups.find((g) => g.group === command.group);
    if (!bucket) {
      bucket = { group: command.group, items: [] };
      groups.push(bucket);
    }
    bucket.items.push({ command, index });
  });

  return createPortal(
    <div
      ref={dialogRef}
      data-afterform-overlay=""
      className="fixed inset-0 z-50 flex items-start justify-center px-4 pt-[10vh] sm:px-6"
      role="dialog"
      aria-modal="true"
      aria-label={t("commandPalette.searchPlaceholder")}
    >
      <button
        type="button"
        tabIndex={-1}
        aria-label={t("commandPalette.close")}
        onClick={() => setOpen(false)}
        className="absolute inset-0 cursor-default bg-[var(--nav-scrim)]"
      />

      <Surface
        tone="raised"
        radius="lg"
        border="none"
        shadow="soft"
        className="relative flex w-full max-w-[720px] flex-col overflow-hidden"
      >
        <div className="studio-command-search flex min-h-11 items-center gap-2 border-b border-line-soft px-3">
          <SearchIcon className="size-4 shrink-0 text-ink-muted" />
          <TextField variant="plain"
            ref={inputRef}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={onKeyDown}
            placeholder={t("commandPalette.searchPlaceholder")}
            aria-label={t("commandPalette.searchPlaceholder")}
            role="combobox"
            aria-expanded="true"
            aria-controls="command-palette-results"
            aria-autocomplete="list"
            aria-activedescendant={
              filtered[activeIndex] ? `command-${filtered[activeIndex].id}` : undefined
            }
            className="h-11 min-h-11 min-w-0 flex-1 rounded-none border-none bg-transparent px-0 py-0 font-sans text-base text-ink placeholder:text-ink-muted sm:min-h-11 sm:text-sm"
          />
          <IconButton
            size="sm"
            onClick={() => setOpen(false)}
            aria-label={t("commandPalette.close")}
            className="text-ink-muted"
          >
            <CloseIcon className="size-4" />
          </IconButton>
        </div>

        <div id="command-palette-results" className="max-h-[58vh] overflow-y-auto p-3" role="listbox" aria-label={t("commandPalette.searchPlaceholder")}>
          {filtered.length === 0 ? (
            <Text variant="body" className="px-3 py-6 text-center text-ink-faint">
              {t("commandPalette.noResults")}
            </Text>
          ) : (
            groups.map((bucket) => (
              <div key={bucket.group} className="mb-3 last:mb-0">
                <Text variant="label" as="div" className="px-3 pb-2 pt-3 text-[11px] font-semibold tracking-[0.1em] text-ink-soft">
                  {bucket.group}
                </Text>
                {bucket.items.map(({ command, index }) => {
                  const active = index === activeIndex;
                  return (
                    <button
                      key={command.id}
                      id={`command-${command.id}`}
                      type="button"
                      role="option"
                      tabIndex={-1}
                      aria-selected={active}
                      onPointerMove={() => setActiveIndex(index)}
                      onClick={() => run(command)}
                      className={cn(
                        "studio-dropdown-option flex min-h-11 w-full items-center gap-3 rounded-md px-4 text-left font-sans text-[14px] transition-colors duration-150 ease-house focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-[var(--focus-ring)]",
                        active ? "bg-accent-subtle font-medium text-accent-text" : "text-ink",
                      )}
                    >
                      <span className="min-w-0 flex-1 truncate">{command.title}</span>
                      {command.shortcut ? (
                        <KbdGroup>
                          {formatShortcut(command.shortcut).map((token, i) => (
                            <Kbd key={i}>{token}</Kbd>
                          ))}
                        </KbdGroup>
                      ) : null}
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </div>

        <div className="studio-command-hints flex min-h-8 flex-wrap items-center gap-x-4 gap-y-1 border-t border-line-soft bg-canvas-soft px-3 py-1.5 text-ink-soft">
          <span className="flex items-center gap-1 text-[11px] font-medium">
            <KbdGroup><Kbd>↑</Kbd><Kbd>↓</Kbd></KbdGroup>
            {t("commandPalette.navigate")}
          </span>
          <span className="flex items-center gap-1 text-[11px] font-medium">
            <Kbd>↵</Kbd>
            {t("commandPalette.select")}
          </span>
          <span className="flex items-center gap-1 text-[11px] font-medium">
            <Kbd>{localeText("interface.esc_abb761d9", { defaultValue: "esc" })}</Kbd>
            {t("commandPalette.close")}
          </span>
        </div>
      </Surface>
    </div>,
    document.body,
  );
}
